import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { LazyImageProps } from "../../@types/Components";
import { checkIfImageExists, getImageListItemStyle } from "../../utils/imgUtils";

/**
 * Represents a singular image that can get used in the ProductGallery component.
 * If no source path was provided, the imagepath will get fetched according to the underlying database/api structures.
 * While the image is actively being loaded, the MUI Skeleton will get displayed to let the user know that something is coming.
 * 
 * @param {LazyImageProps} props object containing the id and name of the product, as well as optional values for the source path, the alternative title and a style object.
 * @returns {JSX.Element} the image which has finished loading its resources or a Skeleton placeholder item.
 */
const LazyImage = (props: LazyImageProps): JSX.Element => {
  const intl = useIntl();
  const imgStyle = getImageListItemStyle();
  const {
    item,
    style = {}
  } = props;
  // Getting the image path from our product.
  const [imageURL, setImageURL] = useState<string>(item.previewImagePath);
  // Creating an alternative text if the image might be missing.
  const alt: string = intl.formatMessage({id: "ProductGallery.imageMissing"}, {title: item.name});
  // Keeping track of the loading status of the referenced image.
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(imageURL === "" ? false : true);

  // Check if product.previewImagePath is non empty and different from our url.
  useEffect(() => {
    if(item.previewImagePath !== "" && imageURL !== item.previewImagePath) {
      setImageURL(item.previewImagePath);
      setIsImageLoaded(true);
    }
    if(isImageLoaded && !checkIfImageExists(imageURL)) {
      setIsImageLoaded(false);
    } 
  }, [imageURL, isImageLoaded, item]);

  return (
    <>
      {
        isImageLoaded ?
          <img
            id={`img_id-${item.id}`}
            key={`${item.name}`}
            src={imageURL}
            alt={alt}
            loading="lazy"
            style={{
              ...imgStyle,
              ...style,
            } as React.CSSProperties}
          />
          :
          <img
            id={`img_id-${item.id}-missing`}
            key={`${item.name}`}
            src={"img/placeholder.jpg"}
            loading="lazy"
            style={{
              ...imgStyle,
              ...style,
            } as React.CSSProperties}
          />
      }
    </>
  );
};

export default LazyImage;